import {
  grammarAnswer,
  type GrammarTeaching,
  type GrammarAnswer,
} from "../content/lianxi/yufa-teaching.js";
import type { ItemAnswer } from "./yufa-marking.js";

/** Resolve only a reviewed answer form. Unknown rewrites receive no diagnosis. */
export function answerFeedback(answer: ItemAnswer): GrammarAnswer | undefined {
  if (answer.kind === "gap" || answer.kind === "arrange") {
    return undefined;
  }
  const text =
    answer.kind === "transform" || answer.kind === "repair"
      ? answer.typed
      : answer.kind === "choose"
        ? answer.options?.[answer.chosen]
        : answer.choices.find((choice) => choice.value === answer.chosen)?.text;
  const language =
    answer.kind === "interpretation" || answer.kind === "reply"
      ? answer.choices.find((choice) => choice.value === answer.chosen)
          ?.language
      : undefined;
  return answer.teaching === undefined || text === undefined
    ? undefined
    : grammarAnswer(answer.teaching, text, language);
}

/** Teaching data travels with the browser's and server's answer keys. */
export interface TaughtAnswer {
  readonly teaching?: GrammarTeaching;
}

/** Use the checked explanation for known forms and a neutral fallback for everything else. */
export function feedbackLine(
  answer: ItemAnswer,
  passed: boolean,
): string | undefined {
  if (answer.kind === "gap" || answer.kind === "arrange") {
    return undefined;
  }
  if (answer.kind === "choose" && answer.teaching === undefined) {
    return undefined;
  }
  const known = answerFeedback(answer);
  if (answer.kind === "repair" && known?.status === "target" && !passed) {
    return "This sentence matches a checked answer. Check which part of the original needs changing, or choose No change needed.";
  }
  if (known === undefined || known.status === "unverified") {
    return passed
      ? undefined
      : "This response has not been checked. Compare it with the checked answer and try the requested construction.";
  }
  const label =
    known.status === "target"
      ? known.language === "en"
        ? "Correct."
        : "Correct 对."
      : known.status === "valid-other"
        ? answer.kind === "reply"
          ? `${known.language === "en" ? "" : "Valid Chinese. "}This reply does not fit the situation.`
          : known.language === "en"
            ? "This interpretation expresses a different meaning."
            : "Valid Chinese. This task asks for something else."
        : "This answer does not fit the task.";
  return `${label} ${known.status === "target" ? withoutVerdict(known.feedback) : known.feedback}`;
}

/**
 * A right answer's reviewed explanation without the "Correct" it may open
 * with, since the line already opens with Correct 对 (CB-23). "Correct: 有 is
 * negated with 没" followed it as "Correct 对. Correct: 有 is negated…".
 */
export function withoutVerdict(feedback: string): string {
  const rest = feedback.replace(/^Correct\b[.:,!]?\s*/u, "");

  return rest === feedback || rest === ""
    ? feedback
    : `${rest.charAt(0).toUpperCase()}${rest.slice(1)}`;
}
